import { useCallback, useMemo } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Button, CircularProgress } from '@mui/material';
import { useLocation, useNavigate, useResolvedPath } from 'react-router-dom';

import {
  BUTTON_MENU_BUTTON_SX,
  BUTTON_MENU_ERROR_ICON_SX,
  BUTTON_MENU_SUCCESS_ICON_SX,
} from '../constants';
import { isMenuOptionActive, mergeSxProps } from '../utils';

import type { ReactNode } from 'react';
import type { FButtonMenuItemProps } from '../types';

export default function FButtonMenuItem({
  option,
  activeVariant,
  buttonVariant,
  className,
  disabled,
  errorLabel,
  navigateOptions,
  successLabel,
}: FButtonMenuItemProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const resolvedPath = useResolvedPath(option.to);
  const isActive = isMenuOptionActive(location, resolvedPath, option.end);
  const { sx: buttonSx, ...restButtonProps } = option.buttonProps ?? {};
  const isDisabled = disabled || Boolean(option.disabled);

  const handleClick = useCallback(() => {
    navigate(option.to, navigateOptions);
  }, [navigate, navigateOptions, option.to]);

  const mergedButtonSx = useMemo(() => mergeSxProps(BUTTON_MENU_BUTTON_SX, buttonSx), [buttonSx]);

  let dataStatus: 'error' | 'success' | undefined;
  if (option.error) {
    dataStatus = 'error';
  } else if (option.success) {
    dataStatus = 'success';
  }

  let statusIcon: ReactNode = null;
  if (option.loading) {
    statusIcon = (
      <CircularProgress size={14} sx={{ ml: 0.5 }} aria-label="Extracting card details" />
    );
  } else if (option.error) {
    statusIcon = (
      <ErrorIcon fontSize="small" aria-label={errorLabel} role="img" sx={BUTTON_MENU_ERROR_ICON_SX} />
    );
  } else if (option.success) {
    statusIcon = (
      <CheckCircleIcon
        fontSize="small"
        aria-label={successLabel}
        role="img"
        sx={BUTTON_MENU_SUCCESS_ICON_SX}
      />
    );
  }

  return (
    <Button
      {...restButtonProps}
      aria-current={isActive ? 'page' : undefined}
      aria-invalid={option.error ? true : undefined}
      className={className}
      color={option.error ? 'error' : 'inherit'}
      data-status={dataStatus}
      disabled={isDisabled}
      onClick={handleClick}
      sx={mergedButtonSx}
      variant={isActive ? activeVariant : buttonVariant}
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {option.label}
        {statusIcon}
      </Box>
      <Box component="span" data-status-indicator aria-hidden />
    </Button>
  );
}
